<template>
  <b-form @submit.prevent="updateStatus()">
    <b-form-row v-if="!shipping?.status || shipping?.status && statusesMap[shipping?.status]?.length > 0">
      <div class="form-group col-sm-8">
        <label for="change-status" v-translate>Change Shipment Status</label>
        <div class="input-group">
          <b-form-select v-model="newShippingStatus" :options="shipping?.status ? statusesMap[shipping?.status] : (ecosystemConfig.collectionStatuses || Object.keys(statusesMap))" class="mb-2 mr-sm-2 mb-sm-0" required id="change-status">
            <template #first>
              <b-form-select-option :value="null" v-translate>-- Select an option --</b-form-select-option>
            </template>
          </b-form-select>
        </div>
        <div v-if="newShippingStatus === 'READY' || newShippingStatus === 'WAITING'">
          <b-form-group class="mt-2">
            <label for="scheduled-at" class="d-block" v-translate>Schedule Date (optional)</label>
            <b-form-input type="date" v-model="reScheduledAt" class="form-control" style="width: auto; display: inline-block" />
            <b-form-select v-model="reScheduledAtHour" :options="timeSlots" style="width: auto; display: inline-block"></b-form-select>
          </b-form-group>
        </div>
        <div v-if="newShippingStatus === 'CONFIRMED' && (!courier || !courier.id)">
          <div class="invalid-feedback d-block mb-3" v-translate>
            CONFIRMED status requires to search and select a courier above before submit the new status.
          </div>
        </div>
        <div v-if="newShippingStatusNeedReason">
          <div class="invalid-feedback d-block mb-3" v-translate="{status: newShippingStatus}">
            %{status} status requires to select/write a reason below before submit the new status.
          </div>
          <b-form-group>
            <label for="status-reason" v-translate>Status Reason</label>
            <b-form-radio-group stacked id="status-reason" v-model="newShippingStatusReason" :options="newShippingStatusReasons" required>
              <template #first>
                <b-form-radio v-if="newShippingStatus === 'TOBERETURNED'" :value="{reason: 'max_retries_reached'}" class="w-100"><translate>Maximum retries reached</translate></b-form-radio>
              </template>
              <b-form-radio v-if="newShippingStatus === 'CANCELED'" :value="{reason: 'created_by_mistake'}"><translate>Created by mistake</translate></b-form-radio>
              <b-form-radio :value="{reason: 'other'}"><translate>Others</translate></b-form-radio>
            </b-form-radio-group>
          </b-form-group>
          <b-form-group v-if="newShippingStatusReason.reason === 'other'">
            <label for="reason-description" v-translate>Reason Description</label>
            <div class="input-group">
              <b-form-textarea
                id="reason-description"
                v-model="newShippingStatusMeta"
                rows="3"
                max-rows="6"
                :state="validateIncidenceDesc"
                required
              />
              <b-form-invalid-feedback :state="validateIncidenceDesc" v-translate="{status: newShippingStatus}">
                "Others" %{status} status requires a description. Please be brief but descriptive.
              </b-form-invalid-feedback>
              <b-form-valid-feedback :state="validateIncidenceDesc" v-translate>
                Alright.
              </b-form-valid-feedback>
            </div>
          </b-form-group>
        </div>
        <div v-if="newShippingStatus === 'DELIVERED'">
          <br>
          <label for="receiver_type_id" v-translate>Receiver Type</label>
          <div class="input-group">
            <b-form-select v-model="deliveredStatusDetailFields.receiver_type_id" :options="receiverTypes.map(x => { return {text: receiverTypesStrings[x.name], value: x.id} })" class="mb-2 mr-sm-2 mb-sm-0" id="receiver_type_id">
              <template #first>
                <b-form-select-option :value="null" v-translate>-- Select an option --</b-form-select-option>
              </template>
            </b-form-select>
          </div>
          <br>
          <label for="receiver_first_name" v-translate>Receiver First Name</label>
          <div class="input-group">
            <b-form-input v-model="deliveredStatusDetailFields.receiver_first_name" class="mb-2 mr-sm-2 mb-sm-0" id="receiver_first_name" required />
          </div>
          <br>
          <label for="receiver_last_name" v-translate>Receiver Last Name</label>
          <div class="input-group">
            <b-form-input v-model="deliveredStatusDetailFields.receiver_last_name" class="mb-2 mr-sm-2 mb-sm-0" id="receiver_last_name" required />
          </div>
          <br>
          <label for="receiver_address_street" v-translate>Address Street</label>
          <div class="input-group">
            <b-form-input v-model="deliveredStatusDetailFields.receiver_address_street" class="mb-2 mr-sm-2 mb-sm-0" id="receiver_address_street" required />
          </div>
          <br>
          <label for="receiver_address_number" v-translate>Address Number</label>
          <div class="input-group">
            <b-form-input v-model="deliveredStatusDetailFields.receiver_address_number" class="mb-2 mr-sm-2 mb-sm-0" id="receiver_address_number" required />
          </div>
          <br>
          <label for="receiver_address_extra" v-translate>Address Extra</label>
          <div class="input-group">
            <b-form-input v-model="deliveredStatusDetailFields.receiver_address_extra" class="mb-2 mr-sm-2 mb-sm-0" id="receiver_address_extra" />
          </div>
          <br>
          <label for="receiver_phone" v-translate>Receiver Phone</label>
          <div class="input-group">
            <b-form-input v-model="deliveredStatusDetailFields.receiver_phone" class="mb-2 mr-sm-2 mb-sm-0" id="receiver_phone" required />
          </div>
        </div>
      </div>
      <div class="form-group col-sm-4">
        <label for="from_extra">&nbsp;</label>
        <div class="input-group">
          <b-button type="submit" :disabled="disableSetStatus || isLoading">
            <b-spinner small v-if="isLoading" />
            <translate>Submit</translate>
          </b-button>
        </div>
      </div>
    </b-form-row>
  </b-form>
</template>

<script>
import constants from '@/common/constants'

export default {
  emits: ['updated'],
  name: 'ShippingStatsUpdate',
  props: {
    shipping: {
      type: Object,
      default: () => {}
    },
    courier: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      isLoading: false,
      newShippingStatus: null,
      reScheduledAt: null,
      reScheduledAtHour: '00:00',
      timeSlots: new Array(24 * 2 + 1).fill(0).map((_, i) => {
        return {
          text: ('0' + ~~(i / 2) + ':0' + 60 * (i / 2 % 1)).replaceAll(/\d(\d\d)/g, '$1'),
          value: ('0' + ~~(i / 2) + ':0' + 60 * (i / 2 % 1)).replaceAll(/\d(\d\d)/g, '$1')
        }
      }),
      newShippingStatusReason: {},
      newShippingStatusMeta: null,
      deliveredStatusDetailFields: {
        receiver_type_id: null
      }
    }
  },
  computed: {
    validateIncidenceDesc() {
      return this.newShippingStatusMeta !== null && this.newShippingStatusMeta.length > 10
    },
    newShippingStatusNeedReason() {
      return this.statusNeedReason.includes(this.newShippingStatus)
    },
    newShippingStatusReasons() {
      if (this.newShippingStatus === 'CANCELED' || this.newShippingStatus === 'RETURNED') {
        return this.shippingStatusReasons[Object.entries(this.statusEvents).find(x => x[1].includes(this.newShippingStatus))[0]].map(x => {
          return {
            text: this.statusReasonsStrings[x.name],
            value: { reason: x.name, reason_id: x.reason_id }
          }
        })
      } else if (this.shipping?.status) {
        return this.shippingStatusReasons[Object.entries(this.statusEvents).find(x => x[1].includes(this.shipping.status))[0]].map(x => {
          return {
            text: this.statusReasonsStrings[x.name],
            value: { reason: x.name, reason_id: x.reason_id }
          }
        })
      }
    },
    disableSetStatus() {
      return (this.newShippingStatus === 'CONFIRMED' && (!this.courier || !this.courier.id)) ||
        (this.newShippingStatusNeedReason && (!this.newShippingStatusReason.reason || this.newShippingStatusReason.reason === 'other' && !this.validateIncidenceDesc))
    }
  },
  watch: {
    'deliveredStatusDetailFields.receiver_type_id': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal && newVal !== null) {
          if (this.receiverTypes.find(x => x.id === newVal).name === 'same_person_or_family') {
            this.deliveredStatusDetailFields.receiver_first_name = this.shipping.to_contact
            this.deliveredStatusDetailFields.receiver_address_street = this.shipping.address_to.formatted_address
            this.deliveredStatusDetailFields.receiver_address_extra = this.shipping.to_extra
            this.deliveredStatusDetailFields.receiver_phone = this.shipping.to_phone
            this.deliveredStatusDetailFields.receiver_signature = 'manual'
          } else {
            this.deliveredStatusDetailFields = {
              receiver_type_id: newVal
            }
          }
        }
      },
      deep: true
    }
  },
  mixins: [constants],
  mounted () {
    this.deliveredStatusDetailFields = {
      ...this.deliveredStatusDetailFields,
      ...{
        receiver_first_name: '-',
        receiver_last_name: '-',
        receiver_address_street: '-',
        receiver_address_number: '-',
        receiver_address_extra: '-',
        receiver_phone: '-',
        receiver_signature: 'manual'
      }
    }
  },
  methods: {
    async updateStatus() {
      const ids = this.shipping?.id || this.collection.map(x => x.id)
      let params
      if (!Array.isArray(ids)) {
        if (this.newShippingStatus === 'READY' && this.reScheduledAt) {
          this.reScheduledAt += ' ' + this.reScheduledAtHour + ':00'
          const utcDate = this.moment(this.reScheduledAt).utc().format('YYYY-MM-DD HH:mm:ss')

          params = {
            shipping_id: ids,
            user_id: this.profile.id,
            status: this.newShippingStatus,
            meta: 'RESCHEDULED AT ' + utcDate + ' UTC'
          }

          this.$http.post(this.$appConfig.api.baseUrl + 'status', params)
            .then(response => {
              const shipping = {
                scheduled_date: utcDate,
                instructions: 'RESCHEDULED ' + this.shipping.instructions
              }
              this.$http.patch(this.$appConfig.api.baseUrl + 'shippings/' + this.shipping.id, shipping)
                .then(() => {
                  this.$emit('updated', true)
                  this.reScheduledAt = null
                  this.newShippingStatus = null
                  this.newShippingStatusMeta = null
                  this.newShippingStatusReason = {}
                })
            })
        } else {
          params = {
            shipping_id: ids,
            user_id: this.profile.id,
            status: this.newShippingStatus,
            meta: this.newShippingStatusMeta || null
          }
          if (this.newShippingStatusNeedReason) {
            params.details = this.newShippingStatusReason
          }
          if (this.newShippingStatus === 'CONFIRMED' && this.courier?.id) {
            params.courier_id = this.courier.id
          }
          if (this.newShippingStatus === 'DELIVERED') {
            params.details = this.deliveredStatusDetailFields
            if (!params.details.receiver_type_id) {
              params.details.receiver_type_id = this.receiverTypes.find(x => x.name === 'same_person_or_family').id
            }
          }
          if (params.details?.reason_id) {
            params.shipping_status_reasons_id = params.details.reason_id
          }
          this.$http.post(this.$appConfig.api.baseUrl + 'status', params)
            .then(() => {
              setTimeout(function () {
                // TODO: emit shipping updated
                this.$emit('updated', true)
                this.newShippingStatus = null
                this.newShippingStatusMeta = null
                this.newShippingStatusReason = {}
              }.bind(this), 1000)
            })
        }
      } else if (this.newShippingStatus !== 'CONFIRMED' || (this.newShippingStatus === 'CONFIRMED' && this.statusCourier?.id)) {
        if (
          confirm(this.$gettext('Are you sure you want to CHANGE the current Collection statuses?'))
        ) {
          this.isLoading = true
          const chunkSize = 40
          const completeCollection = [...new Set(ids)]
          const total = completeCollection.length
          const chunks = [...new Array(Math.ceil(total / chunkSize))].map(_ => completeCollection.splice(0, chunkSize))
          let successCount = 0
          const errors = []
          for (const chunk of chunks) {
            try {
              await this.doUpdateStatus(chunk)
              successCount += chunk.length
            } catch (error) {
              errors.push({
                message: (error && error.body && error.body.message) || this.$gettext('Unknown error'),
                count: chunk.length,
                ids: chunk.join(',')
              })
            }
          }

          this.openStatusFeedback(successCount, total, errors)

          setTimeout(() => {
            this.$root.$emit('bv::refresh::table', 'my-table')
            this.$emit('updated', true)
          }, 100)
          this.reScheduledAt = null
          this.newShippingStatus = null
          this.newShippingStatusMeta = null
          this.newShippingStatusReason = {}
          this.isLoading = false
        }
      } else {
        alert(this.$gettext('You MUST select a courier before change Shippings Status to CONFIRMED'))
      }
    },
    async doUpdateStatus(shippingCollection) {
      let params
      this.reScheduledAt = this.reScheduledAt ? this.reScheduledAt + ' 00:00:00' : null
      if (this.newShippingStatus === 'READY' && this.reScheduledAt) {
        const utcDate = this.moment(this.reScheduledAt).utc().format('YYYY-MM-DD HH:mm:ss')
        console.log(utcDate)
        params = {
          shippings: shippingCollection,
          user_id: this.profile.id,
          status: this.newShippingStatus,
          meta: 'RESCHEDULED AT ' + utcDate + ' UTC'
        }

        this.$http.post(this.$appConfig.api.baseUrl + 'shippings/statuses', params)
          .then(response => {
            const statuses = response.body
            for (let i = 0; i < statuses.length; i++) {
              const shipping = {
                scheduled_date: utcDate,
                instructions: 'RESCHEDULED ' + this.shippingViewObject.find(x => x.id === statuses[i].shipping_id).instructions
              }
              this.$http.patch(this.$appConfig.api.baseUrl + 'shippings/' + shippingCollection[i], shipping)
            }
          })
      } else {
        params = {
          shippings: shippingCollection,
          user_id: this.profile.id,
          status: this.newShippingStatus,
          meta: this.newShippingStatusMeta || null
        }
        if (this.newShippingStatus === 'CONFIRMED' && this.courier?.id) {
          params.courier_id = this.courier.id
        }
        if (this.newShippingStatusNeedReason) {
          params.details = this.newShippingStatusReason
        }
        if (this.newShippingStatus === 'DELIVERED') {
          params.details = this.deliveredStatusDetailFields
          if (!params.details.receiver_type_id) {
            params.details.receiver_type_id = this.receiverTypes.find(x => x.name === 'same_person_or_family').id
          }
        }
        if (params.details?.reason_id) {
          params.shipping_status_reasons_id = params.details.reason_id
        }

        return this.$http.post(this.$appConfig.api.baseUrl + 'shippings/statuses', params)
      }
    },
    openStatusFeedback(successCount, totalCount, errors) {
      let obj
      if (errors.length === 0) {
        obj = {
          title: this.$gettext('SUCCESS'),
          message: this.$gettextInterpolate(this.$gettext('%{count} shipment/s status have been changed'), { count: successCount }),
          type: 'success'
        }
      } else {
        const errorsString = [...new Set(errors.map(x => x.message))]
        const errorsCollection = errorsString.map(message => {
          const matching = errors.filter(y => y.message === message)
          return {
            message,
            count: matching.reduce((acc, y) => acc + y.count, 0),
            ids: matching.map(y => y.ids).join(',')
          }
        })
        obj = {
          title: this.$gettext('WARNING'),
          message: this.$gettextInterpolate(this.$gettext('%{successCount} of %{totalCount} shipment/s status were changed, but there were %{count} errors to review.<br><br>Error types:<br>%{errorList}'), {
            successCount,
            totalCount,
            count: errorsCollection.reduce((acc, x) => acc + x.count, 0),
            errorList: errorsCollection.map(x => { return x.message + '<br>' }).join('')
          }, true),
          type: 'warning'
        }
      }

      this.$root.$children[0].$refs.simplert.openSimplert(obj)
    }
  }
}
</script>
